import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {
  Select,
  InputLabel,
  FormControl,
  Typography,
  MenuItem,
  Checkbox,
  Box,
  Divider,
  InputAdornment,
  FormControlProps,
  SelectProps,
  Chip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import './ArrSelectDropdown.scss';
import {ISelectItem} from '../../../shared/types';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {ReactComponent as Indeterminate} from '../../../../src/assets/images/icon-checked-indeterminate.svg';
import {ReactComponent as SearchIcon} from '../../../../src/assets/images/icon-search.svg';
import {ReactComponent as CloseIcon} from '../../../assets/images/close-icon.svg';
import {t} from 'i18next';
import ArrButton from '../arr-button/ArrButton';
import ArrTextField from '../arr-text-field/ArrTextField';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ClearIcon from '@mui/icons-material/Clear';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import ArrSelectedItemsPanel from './ArrMultiSelectItemPanel';

// -------------------- Base Props --------------------
interface BaseProps {
  dropdownLabel: string;
  dropdownItems: ISelectItem[];
  dropdownItemKey?: string;
  dropdownItemDisplay?: string;
  disabled?: boolean;
  error?: boolean;
  searchable?: boolean;
  withControls?: boolean;
  onClearAll?: () => void;
  onApply?: (value: ISelectItem | ISelectItem[]) => void;
  sizeVariant?: 'small' | 'medium' | 'large';
  formControlRestProps?: Omit<FormControlProps, 'children'>;
  selectRestProps?: Omit<SelectProps, 'onChange' | 'value' | 'multiple'>;
  customWidth?: string | number;
  withSelectAll?: boolean;
  dataTestId?: string;
  checkboxDataTestId?: string;
  customDisplayLabel?: string;
  tabIndex?: number;
}

type MultiSelectVariant = 'checkbox' | 'chip';

// -------------------- Multi Select Props --------------------
export interface ArrMultiSelectProps extends BaseProps {
  multiple: true;
  selectedValues: ISelectItem[];
  onSelectionChange: (value: ISelectItem[]) => void;
  isShowTooltip?: boolean;
  variant?: MultiSelectVariant;
}

const ArrMultiSelectDropdown: React.FC<ArrMultiSelectProps> = ({
  dropdownLabel,
  dropdownItems,
  dropdownItemKey = 'id',
  dropdownItemDisplay = 'name',
  selectedValues,
  onSelectionChange,
  disabled = false,
  error = false,
  searchable = false,
  withControls = false,
  onClearAll,
  onApply,
  sizeVariant = 'large',
  formControlRestProps,
  selectRestProps,
  customWidth,
  withSelectAll = true,
  dataTestId = 'arr-multi-dropdown-form-control',
  checkboxDataTestId = 'arr-multi-dropdown',
  customDisplayLabel,
  isShowTooltip = true,
  variant = 'checkbox',
  tabIndex,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<ISelectItem[]>(selectedValues);
  const [tempSelectedItem, setTempSelectedItem] =
    useState<ISelectItem[]>(selectedValues);
  const [searchFieldText, setSearchFieldText] = useState('');
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(600);

  const formControlRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!customWidth && formControlRef.current) {
      setMenuWidth(formControlRef.current.offsetWidth);
    }
  }, [customWidth, isDropdownOpen]);

  // Dynamic position & height calculation
  useEffect(() => {
    const updateMenuPositionAndHeight = () => {
      if (!formControlRef.current) return;

      const rect = formControlRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide dropdown direction
      const position =
        spaceBelow < 300 && spaceAbove > spaceBelow ? 'top' : 'bottom';
      setMenuPosition(position);

      // Reserve space: search (50) + select all row (50) + withControls buttons (50)
      const reservedHeight =
        (searchable ? 50 : 0) + 50 + (withControls ? 50 : 0);

      const availableHeight = position === 'bottom' ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(600, availableHeight * 0.8 - reservedHeight);
      setMenuMaxHeight(maxHeight > 0 ? maxHeight : 100); // fallback minimal height
    };

    updateMenuPositionAndHeight();
    window.addEventListener('resize', updateMenuPositionAndHeight);
    window.addEventListener('scroll', updateMenuPositionAndHeight, true);

    return () => {
      window.removeEventListener('resize', updateMenuPositionAndHeight);
      window.removeEventListener('scroll', updateMenuPositionAndHeight, true);
    };
  }, [isDropdownOpen, searchable]);

  useEffect(() => {
    setSelectedItem(selectedValues);
    setTempSelectedItem(selectedValues);
  }, [selectedValues]);

  const currentSelection = withControls ? tempSelectedItem : selectedItem;

  const totalItems = dropdownItems.filter((item) => !item.disabled).length;
  const selectedCount = Array.isArray(currentSelection)
    ? currentSelection.length
    : 0;
  const isAllSelected = selectedCount === totalItems;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalItems;

  const handleItemClick = (item: ISelectItem) => {
    const current = [...currentSelection];
    const exists = current.some(
      (s) => s[dropdownItemKey] === item[dropdownItemKey]
    );
    const newValue = exists
      ? current.filter((s) => s[dropdownItemKey] !== item[dropdownItemKey])
      : [...current, item];

    if (variant === 'chip') {
      // immediate update for chips
      setSelectedItem(newValue);
      onSelectionChange(newValue);
    } else {
      // respect withControls for checkbox
      if (withControls) setTempSelectedItem(newValue);
      else {
        setSelectedItem(newValue);
        onSelectionChange(newValue);
      }
    }
  };

  const handleSelectAllClick = () => {
    const enabledItems = dropdownItems.filter((i) => !i.disabled);
    const allSelected = currentSelection.length === enabledItems.length;
    const newSelection = allSelected ? [] : enabledItems;

    if (withControls) setTempSelectedItem(newSelection);
    else {
      setSelectedItem(newSelection);
      onSelectionChange(newSelection);
    }
  };

  const searchChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchFieldText(event.target.value);
  };

  const handleChipDelete = (item: ISelectItem) => {
    const newValue = selectedItem.filter(
      (s) => s[dropdownItemKey] !== item[dropdownItemKey]
    );
    setSelectedItem(newValue);
    onSelectionChange(newValue);
  };

  const handleCloseAndClear = () => {
    setIsDropdownOpen(false);
    setSearchFieldText('');
  };

  const handleDisable = (): boolean => {
    return Array.isArray(currentSelection)
      ? currentSelection.length === 0
      : true;
  };

  const filterItems = <K extends keyof ISelectItem>(
    items: ISelectItem[],
    query: string,
    displayKey: K
  ): ISelectItem[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return items;

    return items.filter((item) => {
      const value = item[displayKey];
      return (
        typeof value === 'string' &&
        value.trim().toLowerCase().includes(normalizedQuery)
      );
    });
  };

  const normalizeWidth = (val?: string | number) =>
    typeof val === 'number' ? `${val}rem` : val;

  const dynamicWidth = customWidth ? normalizeWidth(customWidth) : undefined;

  const dynamicHeight =
    variant === 'chip'
      ? Math.max(
          56,
          currentSelection.length
            ? 36 * Math.ceil(currentSelection.length / 3) + 12
            : 56
        )
      : undefined;

  return (
    <FormControl
      variant={variant === 'chip' ? 'outlined' : 'filled'}
      ref={formControlRef}
      className={`arr-dropdown-container arr-dropdown-container--${sizeVariant} ${variant === 'chip' ? 'arr-multiselect arr-chip' : ''}`}
      disabled={disabled}
      fullWidth={!dynamicWidth}
      style={{
        width: dynamicWidth,
        minWidth: dynamicWidth,
        maxWidth: dynamicWidth,
        flex: '0 0 auto',
        minHeight: dynamicHeight,
      }}
      error={error}
      data-testid={`wrapper-${dataTestId}`}
      {...formControlRestProps}
    >
      {(sizeVariant === 'large' || selectedItem.length === 0) && (
        <InputLabel
          id={`${dropdownLabel}-label`}
          className="arr-dropdown-label"
        >
          {dropdownLabel}
        </InputLabel>
      )}
      <Select
        open={isDropdownOpen}
        onOpen={() => {
          setIsDropdownOpen(true);
          requestAnimationFrame(() => {
            if (searchable) searchInputRef.current?.focus();
          });
        }}
        onClose={handleCloseAndClear}
        multiple
        sx={
          variant === 'chip'
            ? {
                '& .MuiSelect-select': {
                  minHeight: dynamicHeight,
                  height: 'auto',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                },
              }
            : undefined
        }
        labelId={`${dropdownLabel}-label`}
        value={selectedItem}
        SelectDisplayProps={{'data-testid': `${dataTestId}`}}
        renderValue={(selected) => {
          if (variant === 'chip') {
            return (
              <Box
                className="arr-multiselect-value"
                display="flex"
                flexWrap="wrap"
                gap={0.5}
              >
                {(selected as ISelectItem[]).map((item) => (
                  <Chip
                    tabIndex={-1}
                    key={String(item[dropdownItemKey] ?? '')}
                    label={item[dropdownItemDisplay]}
                    className="arr-multiselect-chip"
                    onClick={(e: any) => e.stopPropagation()}
                    onDelete={(e) => {
                      e.stopPropagation();
                      handleChipDelete(item);
                    }}
                    deleteIcon={
                      <CloseIcon
                        fontSize="small"
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    }
                    data-testid={`arr-multi-select-dropdown-chip-${item[dropdownItemKey]}`}
                  />
                ))}
                <span
                  className="arr-chip-cursor"
                  data-testid={`arr-multi-select-dropdown-`}
                >
                  |
                </span>
              </Box>
            );
          } else {
            return (
              <ArrSelectedItemsPanel
                selectedItems={selected as ISelectItem[]}
                itemDisplay={dropdownItemDisplay}
                sizeVariant={sizeVariant}
                disabled={disabled}
                customDisplayLabel={customDisplayLabel}
                dataTestId={`${dropdownLabel}-selected`}
              />
            );
          }
        }}
        IconComponent={ArrowDropDownIcon}
        disabled={disabled}
        autoWidth={false}
        className={`arr-dropdown-container__arr-dropdown-select arr-dropdown-container__arr-dropdown-select--${sizeVariant}`}
        MenuProps={{
          PaperProps: {
            className: `arr-dropdown-container__arr-dropdown-menu arr-dropdown-container__arr-dropdown-menu--${menuPosition} arr-dropdown-container__arr-dropdown-menu--${sizeVariant}`,
            style: dynamicWidth
              ? {
                  width: dynamicWidth,
                  minWidth: dynamicWidth,
                  maxWidth: dynamicWidth,
                }
              : {width: menuWidth, maxWidth: menuWidth},
          },
          anchorOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'bottom', horizontal: 'left'}
              : {vertical: 'top', horizontal: 'left'},
          transformOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'top', horizontal: 'left'}
              : {vertical: 'bottom', horizontal: 'left'},
          MenuListProps: {autoFocusItem: false},
        }}
        data-testid="arr-multi-select-dropdown"
        tabIndex={tabIndex}
        {...selectRestProps}
      >
        {searchable && (
          <Box
            className="arr-dropdown-search"
            data-testid="arr-dropdown-search"
          >
            <ArrTextField
              fullWidth
              id="dropdown-search"
              dataTestId={`multi-select-dd-${searchFieldText.split('')[0]}`}
              state="enabled"
              placeholder="Search"
              sizeVariant="medium"
              onChange={searchChangeHandler}
              value={searchFieldText}
              inputRef={searchInputRef}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      // keep dropdown open
                      setIsDropdownOpen(true);

                      requestAnimationFrame(() => {
                        try {
                          if (searchFieldText) {
                            const clearBtn = document.querySelector(
                              '[data-testid="clear-button"]'
                            ) as HTMLElement | null;
                            if (clearBtn) {
                              clearBtn.focus();
                              return;
                            }
                          }

                          const selectAllSelector = `arr-multi-select-all-menu-item-${checkboxDataTestId}`;
                          const selectAllEl = document.querySelector(
                            `[data-testid="${selectAllSelector}"]`
                          ) as HTMLElement | null;
                          if (selectAllEl) {
                            selectAllEl.focus();
                            return;
                          }

                          const container = scrollContainerRef.current;
                          if (!container) return;
                          const first = container.querySelector<HTMLElement>(
                            'li[role="option"], [role="menuitem"], button, [tabindex]:not([tabindex="-1"])'
                          );
                          if (first) first.focus();
                        } catch {
                          // ignore
                        }
                      });
                    }
                  },
                  endAdornment: searchFieldText.length ? (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<ClearIcon />}
                        onClick={handleCloseAndClear}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            e.stopPropagation();
                            // prefer select-all after clear button
                            requestAnimationFrame(() => {
                              try {
                                const selectAllSelector = `arr-multi-select-all-menu-item-${checkboxDataTestId}`;
                                const selectAllEl = document.querySelector(
                                  `[data-testid="${selectAllSelector}"]`
                                ) as HTMLElement | null;
                                if (selectAllEl) {
                                  selectAllEl.focus();
                                  return;
                                }

                                const container = scrollContainerRef.current;
                                if (!container) return;
                                const first =
                                  container.querySelector<HTMLElement>(
                                    'li[role="option"], [role="menuitem"], button, [tabindex]:not([tabindex="-1"])'
                                  );
                                if (first) first.focus();
                              } catch {
                                // ignore
                              }
                            });
                          }
                          if (e.shiftKey && e.key === 'Tab') {
                            e.preventDefault();
                            e.stopPropagation();
                            searchInputRef.current?.focus();
                          }
                          if (e.key === 'Enter' || e.code === 'Space') {
                            e.preventDefault();
                            setSearchFieldText('');
                            searchInputRef.current?.focus();
                          }
                        }}
                        ariaLabel="clear"
                        shape="none"
                        size="small"
                        data-testid="clear-button"
                        testId="arr-multi-select-dropdown-clear-icon-button"
                      />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<SearchIcon />}
                        onClick={() => {}}
                        ariaLabel="search"
                        shape="none"
                        size="small"
                        data-testid="search-button"
                        testId="arr-multi-select-dropdown-search-icon-button"
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        {withSelectAll && variant !== 'chip' && (
          <Box>
            <MenuItem
              tabIndex={0}
              value="__select_all__"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAllClick();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  requestAnimationFrame(() => {
                    try {
                      const container = scrollContainerRef.current;
                      if (!container) return;
                      const first = container.querySelector<HTMLElement>(
                        'li[role="option"], [role="menuitem"], button, [tabindex]:not([tabindex="-1"])'
                      );
                      if (first) first.focus();
                    } catch {
                      // ignore
                    }
                  });
                }
                if (e.shiftKey && e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  searchInputRef.current?.focus();
                }
              }}
              data-testid={`arr-multi-select-all-menu-item-${checkboxDataTestId}`}
            >
              <Checkbox
                data-testid={`${checkboxDataTestId}-select-all`}
                className="arr-dropdown-checkbox"
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                icon={<UnCheckedIcon />}
                checkedIcon={<CheckedIcon />}
                indeterminateIcon={<Indeterminate />}
              />
              <Typography
                className="arr-dropdown-option"
                data-testid={`arr-multi-select-dropdown-select-all-${checkboxDataTestId}`}
              >
                {t('common.button.selectAll')}
              </Typography>
            </MenuItem>
            <Divider className="divider-dropdown" />
          </Box>
        )}

        <Box
          className={'arr-dropdown-scroll'}
          ref={scrollContainerRef}
          onKeyDown={(e) => {
            if (e.shiftKey && e.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              requestAnimationFrame(() => {
                searchInputRef.current?.focus();
              });
            }
          }}
          style={{maxHeight: menuMaxHeight, overflowY: 'auto'}}
        >
          {filterItems(
            dropdownItems,
            searchFieldText,
            dropdownItemDisplay ?? ''
          ).map((item, index) => {
            const key = item[dropdownItemKey]?.toString();
            const label = item[dropdownItemDisplay];
            const isSelected = currentSelection?.some(
              (s: ISelectItem) => s[dropdownItemKey] === item[dropdownItemKey]
            );

            return (
              <MenuItem
                key={key}
                tabIndex={0}
                value={key}
                onClick={() => handleItemClick(item)}
                selected={isSelected}
                disabled={!!item.disabled}
                data-testid={`arr-multi-select-dropdown-menu-item-${checkboxDataTestId}`}
              >
                {variant !== 'chip' && (
                  <Checkbox
                    data-testid={`${checkboxDataTestId}-option-${key}`}
                    className="arr-dropdown-checkbox"
                    checked={isSelected}
                    size="small"
                    icon={<UnCheckedIcon />}
                    checkedIcon={<CheckedIcon />}
                  />
                )}
                {isShowTooltip ? (
                  <ArrTooltip content={label} showOnTruncate>
                    <Typography
                      className="arr-dropdown-option"
                      data-testid={`arr-multi-select-dropdown-label-${dataTestId}-${index}`}
                    >
                      {label}
                    </Typography>
                  </ArrTooltip>
                ) : (
                  <Typography
                    className="arr-dropdown-option"
                    data-testid={`arr-multi-select-dropdown-without-tp-${dataTestId}-${index}`}
                  >
                    {label}
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Box>

        {withControls && variant !== 'chip' && (
          <Box
            className="arr-dropdown-buttons"
            data-testid="arr-dropdown-buttons"
          >
            <ArrButton
              variant="text"
              size="small"
              onClick={() => {
                setTempSelectedItem([]);
                onSelectionChange([]);
                onClearAll?.();
              }}
              disabled={handleDisable()}
              dataTestId={`clear-all-button-${dataTestId}`}
            >
              {t('common.button.clearAll')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="small"
              onClick={() => {
                if (tempSelectedItem) {
                  setSelectedItem(tempSelectedItem);
                  onSelectionChange(tempSelectedItem);
                  setIsDropdownOpen(false);
                  onApply?.(tempSelectedItem);
                }
              }}
              disabled={handleDisable()}
              dataTestId={`apply-button-${dataTestId}`}
            >
              {t('common.button.apply')}
            </ArrButton>
          </Box>
        )}
      </Select>
    </FormControl>
  );
};

export default ArrMultiSelectDropdown;
